/**
 * helix-checkpoints.ts
 *
 * CANONICAL 10 CHECKPOINTS — locked 2026-05-26 by Bryan K. Short.
 * This file supersedes all prior CP lists in any spec, code, or doc.
 *
 * CP1  Project (5%)                              | CP2  Need / Pain / Demonstration (20%)
 * CP3  Budget (10%)                              | CP4  Timeline (10%)
 * CP5  Authority — Project / Initiative (15%)    | CP6  Technical and Financial Sign-off (15%)
 * CP7  Org Structure and Influence Map (15%)     | CP8  Competitive Position (5%)
 * CP9  Compliance — IT Internal and Industry-Specific (3%)
 * CP10 Legal — Contracts, SLAs, Terms (2%)
 *
 * TOWER / FLOORS = process metaphor (floor N = CPN, ground floor = CP1, penthouse = CP10)
 * HELIX / DNA   = health metaphor (green / amber / red diagnostic up the spine)
 * Do NOT conflate the two metaphors.
 *
 * Status values:
 *   cleared  = full points awarded
 *   partial  = half points awarded
 *   open     = 0 points (not yet addressed)
 *   na       = excluded from denominator entirely
 */

// ─── Types ────────────────────────────────────────────────────────────────────

import { HEALTH_PALETTE } from "@shared/schema";

export type CheckpointId =
  | 'cp1'
  | 'cp2'
  | 'cp3'
  | 'cp4'
  | 'cp5'
  | 'cp6'
  | 'cp7'
  | 'cp8'
  | 'cp9'
  | 'cp10';

export type CheckpointStatus = 'cleared' | 'partial' | 'open' | 'na';

export type ScoreBucket = 'Early' | 'Developing' | 'Qualified' | 'Strong';

export interface Checkpoint {
  id: CheckpointId;
  number: number;       // 1-10
  name: string;         // short label, e.g. 'Project'
  fullName: string;     // 'CP1 — Project'
  description: string;  // one-line description
  floor: number;        // tower floor 1-10 (CP1 = floor 1 = reception, CP10 = floor 10 = rooftop)
  weight: number;       // max points for scoring
}

/** @deprecated Use Checkpoint instead. Kept for backward compatibility with HelixScoreViz. */
export type CheckpointDef = Checkpoint;

export interface CheckpointStatusMap {
  [key: string]: CheckpointStatus;
}

// ─── Checkpoint definitions ───────────────────────────────────────────────────

export const HELIX_CHECKPOINTS: Checkpoint[] = [
  {
    id: 'cp1',
    number: 1,
    name: 'Project',
    fullName: 'CP1 — Project',
    description: 'Is there a real project, and is this a new logo or an existing customer expansion?',
    floor: 1,
    weight: 5,
  },
  {
    id: 'cp2',
    number: 2,
    name: 'Need / Pain / Demonstration',
    fullName: 'CP2 — Need / Pain / Demonstration',
    description: 'Does the customer have a recognized need, demonstrable pain, or provable business case we can address?',
    floor: 2,
    weight: 20,
  },
  {
    id: 'cp3',
    number: 3,
    name: 'Budget',
    fullName: 'CP3 — Budget',
    description: 'Is there real money, a named source, and a validated line item?',
    floor: 3,
    weight: 10,
  },
  {
    id: 'cp4',
    number: 4,
    name: 'Timeline',
    fullName: 'CP4 — Timeline',
    description: 'Is there a decision window AND an implementation window — both real?',
    floor: 4,
    weight: 10,
  },
  {
    id: 'cp5',
    number: 5,
    name: 'Authority — Project / Initiative',
    fullName: 'CP5 — Authority — Project / Initiative',
    description: 'Do we have direct access to the project or initiative authority who can champion and sponsor this deal?',
    floor: 5,
    weight: 15,
  },
  {
    id: 'cp6',
    number: 6,
    name: 'Technical and Financial Sign-off',
    fullName: 'CP6 — Technical and Financial Sign-off',
    description: 'Have technical evaluation and financial approval gates been identified and engaged?',
    floor: 6,
    weight: 15,
  },
  {
    id: 'cp7',
    number: 7,
    name: 'Org Structure and Influence Map',
    fullName: 'CP7 — Org Structure and Influence Map',
    description: 'Do we understand org structure and the full influence map — including where formal authority and real power diverge?',
    floor: 7,
    weight: 15,
  },
  {
    id: 'cp8',
    number: 8,
    name: 'Competitive Position',
    fullName: 'CP8 — Competitive Position',
    description: 'Do we understand the competitive landscape and where we stand in it?',
    floor: 8,
    weight: 5,
  },
  {
    id: 'cp9',
    number: 9,
    name: 'Compliance — IT Internal and Industry-Specific',
    fullName: 'CP9 — Compliance — IT Internal and Industry-Specific',
    description: 'Do we understand every IT internal and industry-specific compliance requirement that could block or delay the deal?',
    floor: 9,
    weight: 3,
  },
  {
    id: 'cp10',
    number: 10,
    name: 'Legal — Contracts, SLAs, Terms',
    fullName: 'CP10 — Legal — Contracts, SLAs, Terms',
    description: 'Do we understand every barrier to signature — and have a path through each?',
    floor: 10,
    weight: 2,
  },
];

// ─── Score computation ────────────────────────────────────────────────────────

/**
 * Compute weighted score (0–100) from checkpoint statuses.
 * - cleared = full points
 * - partial  = half points
 * - open     = 0 points
 * - na       = excluded; remaining points redistribute proportionally
 */
export function computeScore(statuses: CheckpointStatusMap): number {
  const active = HELIX_CHECKPOINTS.filter(
    (cp) => statuses[cp.id] !== 'na'
  );

  if (active.length === 0) return 0;

  const maxPossible = active.reduce((sum, cp) => sum + cp.weight, 0);
  if (maxPossible === 0) return 0;

  const earned = active.reduce((sum, cp) => {
    const s = statuses[cp.id] ?? 'open';
    if (s === 'cleared') return sum + cp.weight;
    if (s === 'partial') return sum + cp.weight / 2;
    return sum;
  }, 0);

  return Math.round((earned / maxPossible) * 100);
}

/**
 * Get descriptive score bucket label.
 */
export function getScoreBucket(score: number): ScoreBucket {
  if (score >= 86) return 'Strong';
  if (score >= 71) return 'Qualified';
  if (score >= 41) return 'Developing';
  return 'Early';
}

/**
 * Get color for score bucket. Colors come from CANON.md §5 via the
 * canonical HEALTH_PALETTE — bucket labels remain the legacy
 * Strong/Qualified/Developing/Early taxonomy but the hex must match canon.
 */
export function getScoreBucketColor(bucket: ScoreBucket): string {
  switch (bucket) {
    case 'Strong':     return HEALTH_PALETTE.darkGreen;
    case 'Qualified':  return HEALTH_PALETTE.lightGreen;
    case 'Developing': return HEALTH_PALETTE.amber;
    case 'Early':      return HEALTH_PALETTE.orangeRed;
  }
}

/**
 * Build a default empty status map with all checkpoints set to 'open'.
 */
export function emptyStatusMap(): CheckpointStatusMap {
  return Object.fromEntries(
    HELIX_CHECKPOINTS.map((cp) => [cp.id, 'open' as CheckpointStatus])
  );
}

/**
 * Get checkpoint definition by id.
 */
export function getCheckpoint(id: CheckpointId): Checkpoint | undefined {
  return HELIX_CHECKPOINTS.find((cp) => cp.id === id);
}

// ─── Qualification helpers ────────────────────────────────────────────────────

/** CP1–CP5 are the qualification tier (first five floors). */
export const QUALIFICATION_IDS: CheckpointId[] = [
  'cp1', 'cp2', 'cp3', 'cp4', 'cp5',
];

/** CP6–CP10 are the closing requirements tier (upper five floors). */
export const CLOSING_IDS: CheckpointId[] = [
  'cp6', 'cp7', 'cp8', 'cp9', 'cp10',
];

/**
 * Compute qualification % (0–100) based on CP1–CP5.
 * Excludes na checkpoints from denominator.
 */
export function computeQualificationPct(statuses: CheckpointStatusMap): number {
  const active = QUALIFICATION_IDS.filter((id) => statuses[id] !== 'na');
  if (active.length === 0) return 0;

  const cleared = active.reduce((sum, id) => {
    const s = statuses[id] ?? 'open';
    if (s === 'cleared') return sum + 1;
    if (s === 'partial') return sum + 0.5;
    return sum;
  }, 0);

  return Math.round((cleared / active.length) * 100);
}
